/**
 * ChecklistItemRow - Linha de item marcável dentro de um grupo
 *
 * Renderiza checkbox + conteúdo editável inline + thread de comentários
 * (visual da timeline) + botão de exclusão. Suporta drag-and-drop.
 *
 * @version 2.0.0
 * RELEASE: 2026-07-07 v1.0.0 - Criação inicial
 * RELEASE: 2026-07-07 v1.1.0 - Adiciona botão/popover de comentário com @menção
 * RELEASE: 2026-07-07 v1.2.0 - Adiciona drag handle e integração com useSortable
 * RELEASE: 2026-07-07 v2.0.0 - Substitui popover 1:1 por thread completa
 *          (ChecklistItemCommentThread) com N comentários + anexos.
 */
import { useState, useRef, useEffect } from 'react';
import { Trash2, GripVertical, MessageCircle } from 'lucide-react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { ChecklistItem } from '@/hooks/useTaskChecklist';
import { ChecklistItemCommentThread } from './ChecklistItemCommentThread';


interface ChecklistItemRowProps {
  item: ChecklistItem;
  taskId: string;
  tenantId: string;
  readOnly?: boolean;
  onToggle: (isDone: boolean) => void;
  onUpdate: (content: string) => void;
  onDelete: () => void;
  onUpsertComment: (params: {
    itemId: string;
    commentId?: string | null;
    content: string;
    mentionedUserIds: string[];
    attachmentIds?: string[];
    removedAttachmentIds?: string[];
  }) => Promise<string>;
  onDeleteComment: (commentId: string) => Promise<unknown>;
}

export function ChecklistItemRow({
  item,
  taskId,
  tenantId,
  readOnly,
  onToggle,
  onUpdate,
  onDelete,
  onUpsertComment,
  onDeleteComment,
}: ChecklistItemRowProps) {
  // Sortable (drag-and-drop de itens dentro do grupo)
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: item.id,
    data: { type: 'checklist-item', groupId: undefined },
    disabled: readOnly,
  });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Controle de edição inline
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(item.content);
  const inputRef = useRef<HTMLInputElement>(null);

  // Controle de expansão da thread de comentários
  // - Auto-abre quando há comentários (facilita identificar o comentário)
  // - Fica retraído quando não há comentários (mostra só o ícone)
  const commentsCount = item.comments?.length ?? 0;
  const [isCommentsOpen, setIsCommentsOpen] = useState(commentsCount > 0);

  // Se o item recebe seu 1º comentário (via realtime/refresh), abre automaticamente
  const prevCountRef = useRef(commentsCount);
  useEffect(() => {
    if (prevCountRef.current === 0 && commentsCount > 0) {
      setIsCommentsOpen(true);
    }
    prevCountRef.current = commentsCount;
  }, [commentsCount]);

  useEffect(() => {
    if (!editing) setValue(item.content);
  }, [item.content, editing]);

  useEffect(() => {
    if (editing) inputRef.current?.focus();
  }, [editing]);

  const commit = () => {
    const next = value.trim();
    if (next && next !== item.content) {
      onUpdate(next);
    } else {
      setValue(item.content);
    }
    setEditing(false);
  };


  return (
    <div ref={setNodeRef} style={style} className="space-y-1">
      {/* Linha principal do item */}
      <div className="group flex items-center gap-1 py-1.5 px-2 rounded-md hover:bg-muted/40 transition-colors">
        {!readOnly && (
          <button
            type="button"
            className="cursor-grab active:cursor-grabbing text-muted-foreground/40 hover:text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity"
            aria-label="Reordenar item"
            {...attributes}
            {...listeners}
          >
            <GripVertical className="h-3.5 w-3.5" />
          </button>
        )}
        <Checkbox
          checked={item.is_done}
          disabled={readOnly}
          onCheckedChange={(checked) => onToggle(!!checked)}
        />
        {editing ? (
          <input
            ref={inputRef}
            className="flex-1 bg-transparent outline-none text-sm border-b border-primary/40 pb-0.5"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); commit(); }
              if (e.key === 'Escape') { setValue(item.content); setEditing(false); }
            }}
            maxLength={500}
          />
        ) : (
          <button
            type="button"
            className={cn(
              'flex-1 text-left text-sm truncate cursor-text',
              item.is_done && 'line-through text-muted-foreground'
            )}
            onClick={() => !readOnly && setEditing(true)}
          >
            {item.content}
          </button>
        )}

        {/* Ícone de balão de comentário — inline no item.
            Sempre visível; ganha destaque + contador quando há comentários. */}
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className={cn(
            'h-7 min-w-7 gap-1 px-1.5 transition-colors',
            commentsCount > 0
              ? 'text-primary hover:text-primary'
              : 'text-muted-foreground/60 hover:text-foreground opacity-70 group-hover:opacity-100'
          )}
          onClick={() => setIsCommentsOpen((v) => !v)}
          aria-label={commentsCount > 0 ? `${commentsCount} comentário(s)` : 'Comentar'}
          aria-expanded={isCommentsOpen}
        >
          <MessageCircle className="h-3.5 w-3.5" />
          {commentsCount > 0 && (
            <span className="text-[10px] font-medium leading-none">{commentsCount}</span>
          )}
        </Button>

        {!readOnly && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive"
            onClick={onDelete}
            aria-label="Excluir item"
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>

      {/* Thread de comentários (visual idêntico ao da timeline) —
          expansão controlada pelo ícone de balão da linha do item */}
      {isCommentsOpen && (
        <div className="pl-8 pr-2">
          <ChecklistItemCommentThread
            itemId={item.id}
            taskId={taskId}
            tenantId={tenantId}
            comments={item.comments ?? []}
            readOnly={readOnly}
            open={isCommentsOpen}
            onOpenChange={setIsCommentsOpen}
            onUpsert={onUpsertComment}
            onDelete={onDeleteComment}
          />
        </div>
      )}

    </div>
  );
}
